import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export type ProductMode = "lanerunner" | "lr-direct";

const STORAGE_KEY = "lr_active_context";

// Hard-bucket route prefixes — visiting these auto-flips the active context.
// Shared routes (Dashboard, Freight Lab, LR Hub) are intentionally omitted so they preserve state.
const LR_DIRECT_PREFIXES = ["/lr-direct"];
const LANERUNNER_PREFIXES = [
  "/search",
  "/search-trucks",
  "/my-loads",
  "/my-trucks",
  "/post-load",
  "/post-truck",
  "/dispatcher",
  "/atlas-hub",
  "/atlas-hub-carrier",
];

function detectFromPath(pathname: string): ProductMode | null {
  if (LR_DIRECT_PREFIXES.some(p => pathname === p || pathname.startsWith(p + "/"))) return "lr-direct";
  if (LANERUNNER_PREFIXES.some(p => pathname === p || pathname.startsWith(p + "/"))) return "lanerunner";
  return null;
}

function readPersisted(): ProductMode {
  if (typeof window === "undefined") return "lanerunner";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "lr-direct" ? "lr-direct" : "lanerunner";
}

interface ProductContextValue {
  context: ProductMode;
  setContext: (next: ProductMode) => void;
  /** True when the switcher should be shown — any authenticated profile. */
  switcherEnabled: boolean;
}

const ProductCtx = createContext<ProductContextValue | undefined>(undefined);

export function ProductProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const { pathname } = useLocation();

  // Initial: URL bucket wins, then persisted, default lr-direct for shippers on dashboard.
  const [context, setContextState] = useState<ProductMode>(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const fromPath = detectFromPath(path);
    if (fromPath) return fromPath;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return stored === "lr-direct" ? "lr-direct" : "lanerunner";
    if (path === "/shipper-dashboard") return "lr-direct";
    return "lanerunner";
  });

  // Stage 2: carriers are no longer pinned — they switch like brokers.
  const effectiveContext: ProductMode = context;

  // Auto-switch on entering hard-bucket routes.
  useEffect(() => {
    const detected = detectFromPath(pathname);
    if (detected && detected !== context) {
      setContextState(detected);
    }
  }, [pathname, context]);

  // Persist on change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, effectiveContext);
  }, [effectiveContext]);

  const setContext = useCallback((next: ProductMode) => {
    setContextState(next);
  }, []);

  const value = useMemo<ProductContextValue>(() => ({
    context: effectiveContext,
    setContext,
    switcherEnabled: !!profile,
  }), [effectiveContext, setContext, profile]);


  return <ProductCtx.Provider value={value}>{children}</ProductCtx.Provider>;
}

export function useProductContext(): ProductContextValue {
  const ctx = useContext(ProductCtx);
  if (!ctx) {
    // Safe fallback so HMR / out-of-tree renders don't crash.
    return { context: "lanerunner", setContext: () => {}, switcherEnabled: false };
  }
  return ctx;
}

/** Maps a nav href to its product bucket. "shared" items render in both contexts. */
export function navBucketForHref(href: string): "lanerunner" | "lr-direct" | "shared" {
  // Shared
  if (
    href === "/broker-dashboard" ||
    href === "/carrier-dashboard" ||
    href === "/shipper-dashboard" ||
    href === "/atlas-lab" ||
    href === "/social"
  ) {
    return "shared";
  }
  if (href.startsWith("/lr-direct")) return "lr-direct";
  return "lanerunner";
}
